import React, { useEffect, useState } from 'react';
import { Cookie, ShieldCheck, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'mce_cookie_consent';
const CONSENT_VERSION = 3;
const CONSENT_MAX_AGE = 15552000;

const DEFAULT_CHOICES = { marketing: false, analytics: false, preferences: false };
const CATEGORIES = [
  ['marketing', 'Marketing', 'Cibler la publicité'],
  ['analytics', 'Statistiques', 'Analyser les performances'],
  ['preferences', 'Préférences', 'Personnaliser l’affichage'],
];

const readConsent = () => {
  try {
    let stored;
    try { stored = window.localStorage.getItem(STORAGE_KEY); } catch { /* Cookies remain available independently. */ }
    const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(`${STORAGE_KEY}=`));
    const saved = JSON.parse(cookie ? decodeURIComponent(cookie.slice(STORAGE_KEY.length + 1)) : stored || 'null');
    const age = Date.now() - Date.parse(saved?.savedAt);
    return saved?.version === CONSENT_VERSION && age >= 0 && age < CONSENT_MAX_AGE * 1000
      && typeof saved.preferences === 'boolean' && typeof saved.analytics === 'boolean' && typeof saved.marketing === 'boolean' ? saved : null;
  } catch {
    return null;
  }
};

const persistConsent = (choices) => {
  const consent = { version: CONSENT_VERSION, essential: true, session: true, analytics: choices.analytics === true, marketing: choices.marketing === true, preferences: choices.preferences === true, savedAt: new Date().toISOString() };
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent)); } catch { /* Keep the current choice even when storage is blocked. */ }
  try {
    document.cookie = `${STORAGE_KEY}=${encodeURIComponent(JSON.stringify(consent))}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  } catch { /* The choice still applies for the current page. */ }
  window.mceCookieConsent = consent;
  window.dispatchEvent(new CustomEvent('mce:consent-change', { detail: consent }));

  return consent;
};

export default function CookieConsent() {
  const [consent, setConsent] = useState(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [choices, setChoices] = useState(() => readConsent() || DEFAULT_CHOICES);

  useEffect(() => {
    if (consent) {
      window.mceCookieConsent = consent;
    }
    const openSettings = () => {
      setChoices(readConsent() || DEFAULT_CHOICES);
      setSettingsOpen(true);
    };
    window.addEventListener('mce:open-cookie-settings', openSettings);
    return () => window.removeEventListener('mce:open-cookie-settings', openSettings);
  }, [consent]);

  const save = (next) => {
    setConsent(persistConsent(next));
    setSettingsOpen(false);
  };

  return (
    <>
      {!consent && (
        <aside className="cookie-consent-banner" role="dialog" aria-modal="false" aria-labelledby="cookie-consent-title">
          <div className="cookie-consent-icon"><Cookie /></div>
          <div className="cookie-consent-copy">
            <h2 id="cookie-consent-title">Vos préférences de navigation</h2>
            <p>Choisissez les usages que vous autorisez : publicité, statistiques et personnalisation. Les cookies nécessaires au fonctionnement du site restent actifs.</p>
            <Link to="/politique-de-confidentialite">Consulter la politique de confidentialité</Link>
          </div>
          <div className="cookie-consent-actions">
            <button type="button" className="cookie-button-secondary" onClick={() => save(DEFAULT_CHOICES)}>Tout refuser</button>
            <button type="button" className="cookie-button-settings" onClick={() => { setChoices(readConsent() || DEFAULT_CHOICES); setSettingsOpen(true); }}>Personnaliser</button>
            <button type="button" className="cookie-button-primary" onClick={() => save({ marketing: true, analytics: true, preferences: true })}>Tout accepter</button>
          </div>
        </aside>
      )}

      {settingsOpen && (
        <div className="cookie-settings-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSettingsOpen(false); }}>
          <section className="cookie-settings-dialog" role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title">
            <button type="button" className="cookie-settings-close" onClick={() => setSettingsOpen(false)} aria-label="Fermer"><X /></button>
            <header><ShieldCheck /><div><span>PRÉFÉRENCES</span><h2 id="cookie-settings-title">Gérer mes cookies</h2></div></header>
            <p>Les cookies nécessaires restent actifs pour mémoriser vos choix et assurer le fonctionnement du site.</p>
            <div className="cookie-preference-list">
              {CATEGORIES.map(([key, title, description]) => <article key={key}><div><b>{title}</b><small>{description}</small></div><label className="cookie-switch"><input type="checkbox" aria-label={title} checked={choices[key]} onChange={event => setChoices(current => ({ ...current, [key]: event.target.checked }))}/><span /></label></article>)}
              <article><div><b>Gestion de session</b><small>Maintenir la connexion lorsqu’un espace client sera disponible. Mémoriser vos choix de cookies.</small></div><span className="cookie-always-active">Nécessaires</span></article>
            </div>
            <div className="cookie-settings-actions">
              <button type="button" className="cookie-button-primary" onClick={() => save(choices)}>Enregistrer mes choix</button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
